import React from 'react';
import { flattenToAppURL } from '@plone/volto/helpers';
import PropTypes from 'prop-types';

// Retrocompat: hero antigo guardava campos no topo (sem array de slides).
const buildLegacySlide = (data) => ({
  title: data.title,
  description: data.description,
  imageUrl: data.imageUrl,
  imageAlt: data.imageAlt,
  url: data.primaryCtaUrl || data.url,
});

const HeroView = ({ data, isEdit = false }) => {
  const slides = React.useMemo(() => {
    if (Array.isArray(data?.slides) && data.slides.length > 0) {
      return data.slides.filter((s) => s && (s.title || s.imageUrl));
    }
    const legacy = buildLegacySlide(data || {});
    return legacy.title || legacy.imageUrl ? [legacy] : [];
  }, [data]);

  const [idx, setIdx] = React.useState(0);

  React.useEffect(() => {
    if (idx >= slides.length) setIdx(0);
  }, [slides.length, idx]);

  React.useEffect(() => {
    const sec = parseInt(data?.autoRotateSeconds, 10);
    if (isEdit || !sec || slides.length <= 1) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), sec * 1000);
    return () => clearInterval(t);
  }, [data?.autoRotateSeconds, slides.length, isEdit]);

  if (slides.length === 0) {
    return (
      <section className="ag-hero ag-hero--empty">
        <div className="ag-hero__caption">
          <h2 className="ag-hero__title">Hero AgSUS</h2>
          <p className="ag-hero__desc">Adicione slides na barra lateral.</p>
        </div>
      </section>
    );
  }

  const slide = slides[idx] || slides[0];
  const showControls = slides.length > 1;
  // Links gravados no bloco podem vir com o host interno quando o conteúdo é
  // lido pelo servidor; flattenToAppURL normaliza e não afeta links externos.
  const slideUrl = flattenToAppURL(slide.url || slide.primaryCtaUrl || '') || null;
  const linkable = slideUrl && !isEdit;
  const go = (delta) => setIdx((i) => (i + delta + slides.length) % slides.length);
  const bgStyle = slide.imageUrl
    ? { backgroundImage: `url('${slide.imageUrl}')` }
    : undefined;

  return (
    <section className="ag-hero" aria-roledescription="carrossel">
      <div className="ag-hero__slide">
        {/* A foto fica num elemento próprio: no computador ela ocupa o slide
            todo, sob a tarja; no celular aparece inteira, acima da tarja. O
            papel de imagem fica só nela, e não no slide, que tem título e link. */}
        <div
          className="ag-hero__media"
          style={bgStyle}
          role={slide.imageAlt ? 'img' : undefined}
          aria-label={slide.imageAlt || undefined}
        />
        {linkable && (
          <a className="ag-hero__link" href={slideUrl} aria-label={slide.title} />
        )}
        <div className="ag-hero__caption">
          <h2 className="ag-hero__title">{slide.title}</h2>
          {slide.description && (
            <p className="ag-hero__desc">{slide.description}</p>
          )}
          {showControls && (
            <div className="ag-hero__dots" role="tablist" aria-label="Selecionar destaque">
              {slides.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  role="tab"
                  aria-current={i === idx ? 'true' : undefined}
                  aria-label={`Destaque ${i + 1} de ${slides.length}`}
                  className={`ag-hero__dot ${i === idx ? 'is-active' : ''}`}
                  onClick={() => setIdx(i)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      {showControls && (
        <>
          <button
            type="button"
            className="ag-hero__arrow ag-hero__arrow--prev"
            aria-label="Destaque anterior"
            onClick={() => go(-1)}
          >
            <svg aria-hidden="true" focusable="false" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <button
            type="button"
            className="ag-hero__arrow ag-hero__arrow--next"
            aria-label="Próximo destaque"
            onClick={() => go(1)}
          >
            <svg aria-hidden="true" focusable="false" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </>
      )}
    </section>
  );
};

HeroView.propTypes = {
  data: PropTypes.shape({
    slides: PropTypes.array,
    autoRotateSeconds: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    title: PropTypes.string,
    description: PropTypes.string,
    imageUrl: PropTypes.string,
    imageAlt: PropTypes.string,
    url: PropTypes.string,
    primaryCtaUrl: PropTypes.string,
  }).isRequired,
  isEdit: PropTypes.bool,
};

export default HeroView;
